import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Calendar, RefreshCw, ReceiptText, Droplet, Printer, MessageCircle, Trash2 } from "lucide-react";
import { fmtBRL } from "@/lib/api";

/**
 * Row individual de boleto. Delega todas as ações ao parent via callbacks.
 * Extrair este componente permite renderizar centenas de rows sem re-render
 * do painel inteiro a cada ação.
 */
export function BoletoRow({ b, onMarcarPago, onEditarVenc, onRegenerarPix, onAjuste, onIsentarJuros, onImprimir, onCobranca, onExcluir }) {
  const naoPago = b.status !== "PAGO";
  const temEncargos = (Number(b.multa || 0) + Number(b.juros || 0)) >= 0.01;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border border-border p-4 rounded-sm gap-3">
      <div className="min-w-0 flex-1">
        <div className="font-semibold break-anywhere">{b.razao_social}</div>
        <div className="text-xs text-muted-foreground font-mono break-anywhere">
          {b.cnpj} · vencimento {b.vencimento}
          {b.dias_atraso > 0 && (
            <span className="ml-2 text-destructive font-bold">
              · {b.dias_atraso} dia(s) em atraso
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-1 break-anywhere">
          Aluguel Base {fmtBRL(b.valor_aluguel)}
          {b.valor_iptu > 0 && <> + IPTU {fmtBRL(b.valor_iptu)}</>}
          {b.valor_agua > 0 && <> + Água {fmtBRL(b.valor_agua)}</>}
          {" "}+ Rateio {fmtBRL(b.valor_rateio)}
          {(b.multa > 0 || b.juros > 0) && (
            <span className="text-destructive font-semibold">
              {" "}+ Multa {fmtBRL(b.multa)} + Mora {fmtBRL(b.juros)}
            </span>
          )}
        </div>
      </div>
      <div className="sm:text-right flex-shrink-0">
        <div className="stat-num text-xl">{fmtBRL(b.valor_total)}</div>
        <div className="flex items-center gap-2 mt-2 flex-wrap sm:justify-end">
          <Badge variant={b.status === "PAGO" ? "default" : "outline"} className="uppercase text-[9px] tracking-widest">
            {b.status}
          </Badge>
          {naoPago && (
            <Button size="sm" variant="outline" onClick={() => onMarcarPago(b.id)} data-testid={`baixa-${b.id}`}>
              <CheckCircle size={12} className="mr-1" /> Baixa manual
            </Button>
          )}
          {naoPago && (
            <Button size="sm" variant="outline" onClick={() => onEditarVenc(b)}
              data-testid={`editar-venc-${b.id}`} title="Editar data de vencimento deste boleto"
              className="border-info/40 text-info hover:bg-info/5">
              <Calendar size={12} className="mr-1" /> Venc.
            </Button>
          )}
          {naoPago && (
            <Button size="sm" variant="outline" onClick={() => onRegenerarPix(b)}
              data-testid={`regenerar-pix-${b.id}`} title="Descarta QR antigo e re-emite Pix no Banco Inter com valor_total atualizado"
              className="border-warning/40 text-warning hover:bg-warning/5">
              <RefreshCw size={12} className="mr-1" /> Pix
            </Button>
          )}
          {naoPago && (
            <Button size="sm" variant="outline" onClick={() => onAjuste(b)}
              data-testid={`ajuste-${b.id}`} title="Lançar ajuste/resíduo (ex: diferença de rateio de mês anterior)"
              className="border-info/40 text-info hover:bg-info/5">
              <ReceiptText size={12} className="mr-1" /> Ajuste
            </Button>
          )}
          {naoPago && temEncargos && (
            <Button size="sm" variant="outline" onClick={() => onIsentarJuros(b)}
              data-testid={`isentar-juros-${b.id}`}
              title={`Abonar multa (${fmtBRL(Number(b.multa || 0))}) + juros (${fmtBRL(Number(b.juros || 0))}) desta competência`}
              className="border-success/40 text-success hover:bg-success/5">
              <Droplet size={12} className="mr-1" /> Isentar juros
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onImprimir(b)}
            data-testid={`imprimir-boleto-${b.id}`} title="Imprimir comprovante com QR Pix (1/4 ofício)">
            <Printer size={12} className="mr-1" /> Imprimir
          </Button>
          <Button size="sm" variant="outline" onClick={() => onCobranca(b)}
            data-testid={`enviar-cobranca-${b.id}`} title="Enviar cobrança WhatsApp + E-mail"
            className="border-info/40 text-info hover:bg-info/10">
            <MessageCircle size={12} />
          </Button>
          <Button size="sm" variant="outline" onClick={() => onExcluir(b)}
            data-testid={`excluir-boleto-${b.id}`}
            title={b.status === "PAGO" ? "Excluir este boleto PAGO (limpeza de histórico — não afeta cadastros)" : "Excluir este boleto (não afeta cadastros)"}
            className="border-destructive/40 text-destructive hover:bg-destructive/10">
            <Trash2 size={12} />
          </Button>
        </div>
      </div>
    </div>
  );
}
